// src/store/appStore.ts
import { logger } from '@lib/logging-system';
import { type Action, configureStore, createSelector, type ThunkAction } from '@reduxjs/toolkit';
import { loadInitialDeliveryData } from '@store/reducers/deliveryReducer.ts';
import documentReducer, { loadInitialDocumentData } from '@store/reducers/documentReducer.ts';
import driverReducer, { loadInitialDriverData } from '@store/reducers/driverReducer.ts';
import notificationReducer from '@store/reducers/notificationReducer.ts';
import originatorReducer, { loadInitialOriginatorData } from '@store/reducers/originatorReducer.ts';
import orkestroReducer, { loadInitialOrkestroData } from '@store/reducers/orkestroReducer.ts';
import routeReducer from '@store/reducers/routeReducer.ts';
import themeConfigReducer from '@store/reducers/themeConfigReducer.tsx';
import isEqual from 'lodash-es/isEqual';
import {
  type TypedUseSelectorHook,
  useDispatch as useReduxDispatch,
  useSelector as useReduxSelector,
  useStore,
} from 'react-redux';
import { combineReducers } from 'redux';
import { type PersistedState, persistReducer, persistStore } from 'redux-persist';
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE } from 'redux-persist/es/constants';
import storage from 'redux-persist/lib/storage';
import watch from 'redux-watch';

import loggingReducer from '@/lib/logging-system/store/loggingReducer';
import addressesReducer from '@/store/reducers/addressesReducer';
import adminReducer, { loadInitialAdminData } from '@/store/reducers/adminReducer';
import authReducer, { loadInitialAuthData } from '@/store/reducers/authReducer';
import bidRatesReducer from '@/store/reducers/bidRatesReducer';
import billingReducer, { loadInitialBillingData } from '@/store/reducers/billingReducer';
import calendarReducer from '@/store/reducers/calendarReducer';
import configurationReducer, { loadInitialConfigurationData } from '@/store/reducers/configurationReducer';
import contactsReducer from '@/store/reducers/contactsReducer';
import courierAdvanceReducer from '@/store/reducers/courierAdvanceReducer';
import courierPayoutReducer from '@/store/reducers/courierPayoutReducer';
import deliveryReducer from '@/store/reducers/deliveryReducer';
import mapReducer from '@/store/reducers/mapReducer';
import meReducer from '@/store/reducers/meReducer';
import noteReducer, { loadInitialNoteData } from '@/store/reducers/noteReducer';
import userReducer from '@/store/reducers/usersReducer';
import vehicleReducer, { loadInitialVehicleData } from '@/store/reducers/vehicleReducer';

import invoiceReducer from './reducers/invoiceReducer';
import parcelReducer from './reducers/parcelReducer';
import webSocketReducer from './reducers/webSocketReducer';

const documentPersistConfig = {
  key: 'document',
  version: 1,
  storage,
  blacklist: [''],
};

const deliveryPersistConfig = {
  key: 'delivery',
  version: 1,
  storage,
  blacklist: [''],
};

const driverPersistConfig = {
  key: 'driver',
  version: 1,
  storage,
  blacklist: [''],
};

const mapPersistConfig = {
  key: 'map',
  version: 1,
  storage,
  blacklist: [''],
};

const calendarPersistConfig = {
  key: 'calendar',
  version: 1,
  storage,
  blacklist: [''],
};

const bidRatesPersistConfig = {
  key: 'bidRates',
  version: 1,
  storage,
  blacklist: [''],
};

const mePersistConfig = {
  key: 'me',
  version: 1,
  storage,
  blacklist: ['search', 'loading', 'hasErrors', 'error', 'errors'],
};

const notificationPersistConfig = {
  key: 'notification',
  version: 1,
  storage,
  blacklist: [''],
};

const originatorPersistConfig = {
  key: 'originator',
  version: 1,
  storage,
  blacklist: [''],
};

const orkestroPersistConfig = {
  key: 'orkestro',
  version: 1,
  storage,
  blacklist: [''],
};

const routePersistConfig = {
  key: 'route',
  version: 1,
  storage,
  blacklist: ['previousRoute', 'currentRoute'],
};

const themePersistConfig = {
  key: 'themeConfig',
  version: 1,
  storage,
  blacklist: [''],
};

const userPersistConfig = {
  key: 'user',
  version: 1,
  storage,
  blacklist: ['search', 'loading', 'hasErrors'],
};

const vehiclePersistConfig = {
  key: 'vehicle',
  version: 2, // Increment version to trigger migration
  storage,
  blacklist: ['vehicleStatusTypeColourMap', 'updatingVehicleStatuses'],
  migrate: (state: PersistedState) => {
    // Fix corrupted updatingVehicleStatuses
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const stateAsAny = state as any; // Necessary for migration - PersistedState type is too restrictive
    if (stateAsAny?.updatingVehicleStatuses && !Array.isArray(stateAsAny.updatingVehicleStatuses)) {
      logger.info('[appStore] Migrating vehicle state: fixing updatingVehicleStatuses');
      return Promise.resolve({
        ...stateAsAny,
        updatingVehicleStatuses: [],
      });
    }
    return Promise.resolve(state);
  },
};

const notePersistConfig = {
  key: 'note',
  version: 1,
  storage,
  blacklist: [
    'notes',
    'entityNotes',
    'mutationStatus',
    'mutationError',
    'noteableTypesStatus',
    'noteableTypesError',
    'noteCategoriesStatus',
    'noteCategoriesError',
  ],
};

const configurationPersistConfig = {
  key: 'configuration',
  version: 1,
  storage,
  blacklist: [
    'systemConfigurationStatus',
    'systemConfigurationError',
    'companyConfigurationStatus',
    'companyConfigurationError',
  ],
};

const adminPersistConfig = {
  key: 'admin',
  version: 1,
  storage,
  blacklist: [
    'systemSettingsStatus',
    'systemSettingsError',
    'userCompanyCreationStatus',
    'userCompanyCreationError',
    'auditLogStatus',
    'auditLogError',
    'auditLogs',
    'auditLogQuery',
    'lastCreatedUserCompany',
    'impersonationError',
    'featureFlagsStatus',
    'featureFlagsError',
    'subscriptionStatus',
    'subscriptionError',
  ],
};

const authPersistConfig = {
  key: 'auth',
  version: 1,
  storage,
  blacklist: [
    'loginStatus',
    'loginError',
    'twoFactorStatus',
    'twoFactorError',
    'tokenStatus',
    'tokenError',
    'forgotPasswordStatus',
    'forgotPasswordError',
    'resetPasswordStatus',
    'resetPasswordError',
    'twoFactorQrCode',
    'twoFactorSecret',
  ],
};

const billingPersistConfig = {
  key: 'billing',
  version: 1,
  storage,
  blacklist: [
    'billingStatus',
    'billingError',
    'invoiceStatus',
    'invoiceError',
    'transactionStatus',
    'transactionError',
    'paymentMethodStatus',
    'paymentMethodError',
    'lastBillingQuery',
    'lastInvoiceQuery',
    'lastTransactionQuery',
  ],
};

const contactsPersistConfig = {
  key: 'contacts',
  version: 1,
  storage,
  blacklist: [''],
};

const addressesPersistConfig = {
  key: 'addresses',
  version: 1,
  storage,
  blacklist: ['loading', 'error'],
};

const webSocketPersistConfig = {
  key: 'webSocket',
  version: 1,
  storage,
  blacklist: ['connectionState', 'connectionError', 'messages', 'metrics'],
};

const invoicePersistConfig = {
  key: 'invoice',
  version: 1,
  storage,
  blacklist: ['status', 'error'],
};

const rootPersistConfig = {
  key: 'root',
  version: 1,
  storage,
  blacklist: [
    'map',
    'delivery',
    'document',
    'driver',
    'calendar',
    'me',
    'notification',
    'originator',
    'orkestro',
    'route',
    'themeConfig',
    'user',
    'vehicle',
    'note',
    'configuration',
    'admin',
    'auth',
    'billing',
    'contacts',
    'addresses',
    'webSocket',
    'invoice',
    'courierAdvance',
    'courierPayout',
  ],
};

const reducers = combineReducers({
  delivery: persistReducer(deliveryPersistConfig, deliveryReducer),
  document: persistReducer(documentPersistConfig, documentReducer),
  driver: persistReducer(driverPersistConfig, driverReducer),
  map: persistReducer(mapPersistConfig, mapReducer),
  calendar: persistReducer(calendarPersistConfig, calendarReducer),
  bidRates: persistReducer(bidRatesPersistConfig, bidRatesReducer),
  me: persistReducer(mePersistConfig, meReducer),
  notification: persistReducer(notificationPersistConfig, notificationReducer),
  originator: persistReducer(originatorPersistConfig, originatorReducer),
  orkestro: persistReducer(orkestroPersistConfig, orkestroReducer),
  route: persistReducer(routePersistConfig, routeReducer),
  themeConfig: persistReducer(themePersistConfig, themeConfigReducer),
  user: persistReducer(userPersistConfig, userReducer),
  vehicle: persistReducer(vehiclePersistConfig, vehicleReducer),
  parcel: parcelReducer,
  note: persistReducer(notePersistConfig, noteReducer),
  configuration: persistReducer(configurationPersistConfig, configurationReducer),
  admin: persistReducer(adminPersistConfig, adminReducer),
  auth: persistReducer(authPersistConfig, authReducer),
  billing: persistReducer(billingPersistConfig, billingReducer),
  logging: loggingReducer,
  contacts: persistReducer(contactsPersistConfig, contactsReducer),
  addresses: persistReducer(addressesPersistConfig, addressesReducer),
  webSocket: persistReducer(webSocketPersistConfig, webSocketReducer),
  invoice: persistReducer(invoicePersistConfig, invoiceReducer),
  courierAdvance: courierAdvanceReducer,
  courierPayout: courierPayoutReducer,
});

const persistedReducer = persistReducer(rootPersistConfig, reducers);

const appStore = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools: !import.meta.env.PROD,
});
export const appPersistStore = persistStore(appStore);

const w = watch(appStore.getState, 'me.user', isEqual);
appStore.subscribe(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  w(async (newVal: any, oldVal: any) => {
    if (oldVal !== null && newVal === null) {
      logger.info('[appStore] Log out triggered by me.user changing from a value to null');
      await appPersistStore.purge();
    }
  }),
);

export type AppStore = typeof appStore;
export type AppState = ReturnType<AppStore['getState']>;
export type RootState = AppState; // Alias for compatibility
export type AppDispatch = AppStore['dispatch'];

export const useAppDispatch = () => useReduxDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<AppState> = useReduxSelector;

export const createAppSelector = createSelector.withTypes<AppState>();

export const useAppStore: () => AppStore = useStore;

export type ReduxThunkAction<ReturnType = void> = ThunkAction<ReturnType, AppState, unknown, Action>;

export default appStore;

appStore.dispatch(loadInitialDeliveryData());
appStore.dispatch(loadInitialDocumentData());
appStore.dispatch(loadInitialDriverData());
appStore.dispatch(loadInitialOriginatorData());
appStore.dispatch(loadInitialOrkestroData());
appStore.dispatch(loadInitialVehicleData());
appStore.dispatch(loadInitialNoteData());
appStore.dispatch(loadInitialConfigurationData());
appStore.dispatch(loadInitialAdminData());
appStore.dispatch(loadInitialAuthData());
appStore.dispatch(loadInitialBillingData());
